// Minimal, safe Markdown renderers: `renderInlineMarkdown` (single values, e.g.
// itinerary fields) and `renderMarkdown` (multi-line notes — paragraphs, line
// breaks and nested ordered/unordered lists, with the same inline constructs).
//
// The inline renderer supports exactly three constructs (plus bare-URL autolinks):
//   **bold**        → <strong>
//   _italics_       → <em>
//   [text](url)     → <a href rel="noopener noreferrer" target="_blank">
//
// Security model: this output is inserted as innerHTML, so it must be
// XSS-safe. Everything is HTML-escaped FIRST; then the three constructs are
// re-introduced from the escaped text. Link URLs are scheme-validated
// (http/https/mailto only) — anything else (javascript:, data:, etc.) is
// dropped and the link renders as plain text. No raw HTML passthrough; the only
// block elements are the notes renderer's p/br/ul/ol/li.

const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:'];

/**
 * HTML-escape a string for safe insertion as text/attribute content.
 * @param {string} s
 * @returns {string}
 */
export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Validate a link URL. Accepts absolute http/https/mailto URLs; a scheme-LESS URL
 * that looks like a host (e.g. `google.com/x`) is treated as `https://…` for
 * convenience. Returns the normalised href, or null if the scheme is
 * disallowed/unparseable. NB the input is the RAW (un-escaped) url from source.
 * @param {string} rawUrl
 * @returns {string | null}
 */
export function safeUrl(rawUrl) {
  let url = String(rawUrl ?? '').trim();
  if (!url) return null;
  // No scheme (and not a scheme-relative `//host`)? If it looks like a domain
  // (a dot before any slash, no spaces), assume https. This deliberately does
  // NOT rescue disallowed schemes like `javascript:` — those still parse with a
  // protocol and get rejected below.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(url) && !url.startsWith('//')) {
    const host = url.split(/[/?#]/, 1)[0];
    if (!/\s/.test(url) && host.includes('.')) url = `https://${url}`;
    else return null;
  }
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (!ALLOWED_SCHEMES.includes(parsed.protocol)) return null;
  return parsed.href;
}

// Matches, over ALREADY-ESCAPED text, the three inline constructs. Links first
// so a `[**x**](url)` label can still bold inside (we recurse into the label).
// - link:   [label](url)   — label = no unescaped brackets; url = no spaces/parens
// - bold:   **text**       — non-greedy, no ** inside
// - italic: _text_         — non-greedy, no _ inside
const LINK_RE = /\[([^\]]*?)\]\(([^)\s]+)\)/;
const BOLD_RE = /\*\*([^*]+?)\*\*/;
const ITALIC_RE = /_([^_]+?)_/;

// Bare http(s):// URL over ALREADY-ESCAPED text. Stops at whitespace and `<` (so
// it can't cross a tag boundary) and trims a trailing sentence punctuation char
// (`.,;:!?)"'`) so it stays out of the link. Global for replace-all.
const BARE_URL_RE = /https?:\/\/[^\s<]*[^\s<.,;:!?)"']/gi;

/**
 * Auto-link bare http(s):// URLs in an already-escaped, construct-free text run.
 * The URL text is escaped (e.g. `&amp;`); we unescape before URL-parsing so the
 * href is the true URL, then re-escape it once for the attribute. Unsafe schemes
 * can't appear here (the matcher requires http/https).
 * @param {string} escaped already-HTML-escaped, no markdown constructs
 * @returns {string}
 */
function autolinkBareUrls(escaped) {
  return escaped.replace(BARE_URL_RE, (m) => {
    const href = safeUrl(unescapeEntities(m));
    return href
      ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer" target="_blank">${m}</a>`
      : m;
  });
}

/**
 * Render inline Markdown to a safe HTML string.
 * @param {string} text
 * @returns {string} HTML-safe string (may be embedded via innerHTML)
 */
export function renderInlineMarkdown(text) {
  const escaped = escapeHtml(text);
  return parseInline(escaped);
}

/**
 * Tidy a notes value for storage: drop leading blank lines + trailing
 * whitespace (leading indentation on the first line is kept — it may be a
 * nested list). Blank/absent → null (i.e. "no notes").
 * @param {unknown} value
 * @returns {string | null}
 */
export function normaliseNotes(value) {
  if (typeof value !== 'string') return null;
  const s = value.replace(/^(?:[ \t]*\r?\n)+/, '').trimEnd();
  return s ? s : null;
}

// Block-level list markers (over RAW, tab-expanded text). The captured leading
// spaces are the item's indent, used for nesting.
//   unordered: `- x`, `* x`, `+ x`   (a marker char + whitespace — so `**bold**`
//              at the start of a line is NOT a list item)
//   ordered:   `1. x`, `2) x`
const UL_ITEM_RE = /^( *)[-*+]\s+(.*)$/;
const OL_ITEM_RE = /^( *)(\d{1,9})[.)]\s+(.*)$/;

/** Indent (in spaces) a nested list must exceed its parent's by. */
const NEST_INDENT = 2;

/**
 * Render multi-line Markdown notes to a safe HTML string. Builds on the inline
 * renderer (bold, italics, links, bare-URL autolinks — applied per line) and
 * adds the block structure:
 *  - paragraphs, separated by blank lines; a single newline is a line break;
 *  - unordered (`-`/`*`/`+`) and ordered (`1.`/`1)`) lists, nested by indenting
 *    an item 2+ spaces deeper than its parent. An ordered list keeps the number
 *    it starts at (`<ol start>`); later numbers just count on (so writing `1.`
 *    for every step works). A blank line between items keeps the list going; an
 *    indented plain line continues the item above it; an unindented plain line
 *    ends the list.
 * Same security model as the inline renderer: every text run is HTML-escaped
 * before any markup is introduced, and the only tags emitted are p/br/ul/ol/li
 * plus the inline ones.
 * @param {string} text
 * @returns {string} HTML-safe string (may be embedded via innerHTML)
 */
export function renderMarkdown(text) {
  const lines = String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, '    ')
    .split('\n');
  const blocks = []; // { kind: 'p', lines } | { kind: 'list', list }
  const stack = []; // open lists, outermost first
  let para = null;

  // A new list nests under the innermost open list's latest item (or is a
  // top-level block when no list is open).
  const openList = (type, start, indent) => {
    const list = { type, start, indent, items: [] };
    const parent = stack.at(-1);
    if (parent) parent.items.at(-1).children.push(list);
    else blocks.push({ kind: 'list', list });
    stack.push(list);
    return list;
  };

  for (const line of lines) {
    const ul = UL_ITEM_RE.exec(line);
    const ol = ul ? null : OL_ITEM_RE.exec(line);
    if (ul || ol) {
      para = null;
      const type = ul ? 'ul' : 'ol';
      const start = ol ? Number(ol[2]) : 1;
      const indent = (ul ?? ol)[1].length;
      // Dedent closes nested lists (never the outermost: an under-indented item
      // still belongs to the top-level list).
      while (stack.length > 1 && indent < stack.at(-1).indent) stack.pop();
      let list = stack.at(-1);
      if (!list || indent >= list.indent + NEST_INDENT) {
        list = openList(type, start, indent);
      } else if (list.type !== type) {
        // Same level, other kind (bullets → numbers): a sibling list.
        stack.pop();
        list = openList(type, start, indent);
      }
      list.items.push({ lines: [ul ? ul[2] : ol[3]], children: [] });
      continue;
    }
    if (!line.trim()) {
      para = null;
      continue;
    }
    if (stack.length) {
      if (/^ {2,}/.test(line)) {
        stack.at(-1).items.at(-1).lines.push(line.trim());
        continue;
      }
      stack.length = 0;
    }
    if (!para) {
      para = { kind: 'p', lines: [] };
      blocks.push(para);
    }
    para.lines.push(line.trim());
  }

  const inline = (ls) => ls.map((l) => parseInline(escapeHtml(l))).join('<br>');
  const renderList = (list) => {
    const start = list.type === 'ol' && list.start !== 1 ? ` start="${list.start}"` : '';
    const items = list.items
      .map((it) => `<li>${inline(it.lines)}${it.children.map(renderList).join('')}</li>`)
      .join('');
    return `<${list.type}${start}>${items}</${list.type}>`;
  };
  return blocks
    .map((b) => (b.kind === 'p' ? `<p>${inline(b.lines)}</p>` : renderList(b.list)))
    .join('');
}

/**
 * Recursively parse the three constructs over an already-escaped string.
 * We scan for the earliest match among link/bold/italic and recurse on the
 * surrounding + inner segments so nesting (bold inside a link label, etc.)
 * works and no construct is applied inside another's delimiters twice.
 * @param {string} escaped already HTML-escaped text
 * @returns {string}
 */
function parseInline(escaped) {
  if (!escaped) return '';

  // Find the earliest-starting construct.
  const candidates = [
    { kind: 'link', m: LINK_RE.exec(escaped) },
    { kind: 'bold', m: BOLD_RE.exec(escaped) },
    { kind: 'italic', m: ITALIC_RE.exec(escaped) },
  ].filter((c) => c.m);

  // Leaf run (no markdown constructs left) → auto-link any bare URLs in it.
  if (candidates.length === 0) return autolinkBareUrls(escaped);

  candidates.sort((a, b) => a.m.index - b.m.index);
  const { kind, m } = candidates[0];
  const before = escaped.slice(0, m.index);
  const after = escaped.slice(m.index + m[0].length);

  let rendered;
  if (kind === 'link') {
    // m[2] (the url) was HTML-escaped; unescape entities before URL-parsing so
    // e.g. `&amp;` in a query string validates correctly.
    const href = safeUrl(unescapeEntities(m[2]));
    const label = parseInline(m[1]) || escapeHtml(unescapeEntities(m[2]));
    rendered = href
      ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer" target="_blank">${label}</a>`
      : // Invalid scheme → render the original text literally (no link).
        `[${parseInline(m[1])}](${m[2]})`;
  } else if (kind === 'bold') {
    rendered = `<strong>${parseInline(m[1])}</strong>`;
  } else {
    rendered = `<em>${parseInline(m[1])}</em>`;
  }

  return parseInline(before) + rendered + parseInline(after);
}

/**
 * Reverse the subset of HTML entities we produce in escapeHtml, so a URL that
 * contained `&`, `<`, quotes, etc. round-trips for URL parsing.
 * @param {string} s
 * @returns {string}
 */
export function unescapeEntities(s) {
  return String(s ?? '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
